import { formatMoneyFull } from '@/lib/money'
import type { Row } from './PricingTable'

// What a channel's pricing draft changes against the published rows
// (Plan::Pricings::DraftDiff). Keyed by the draft's ids: the review page
// renders the draft's rows and levels and looks up the published "was" value
// per cell.
export interface PricingDiff {
  added_product_ids: string[]
  removed_products: (Row & { node_name: string | null })[]
  moved: Record<string, string | null>
  prices_was: Record<string, Record<string, number | null>>
  asps_was: Record<string, number | null>
  levels: {
    added_ids: string[]
    removed: { id: string; name: string; color: string | null }[]
    renamed: Record<string, string>
  }
  summary: {
    added_products: number
    removed_products: number
    moved_products: number
    changed_prices: number
    changed_asps: number
    added_levels: number
    removed_levels: number
    renamed_levels: number
    any_changes: boolean
  }
}

// The number on the review page's "Plan changes" tab: every kind of change
// counts once, whether it is a cell, a product row, or a price level.
export function pricingChangeCount(summary: PricingDiff['summary']): number {
  return summary.added_products + summary.removed_products + summary.moved_products +
    summary.changed_prices + summary.changed_asps +
    summary.added_levels + summary.renamed_levels + summary.removed_levels
}

export function pricingSummaryText(summary: PricingDiff['summary']): string {
  const parts: string[] = []
  const add = (n: number, one: string, many = `${one}s`) => { if (n > 0) parts.push(`${n} ${n === 1 ? one : many}`) }
  add(summary.added_products, 'product added', 'products added')
  add(summary.removed_products, 'product removed', 'products removed')
  add(summary.moved_products, 'product moved', 'products moved')
  add(summary.changed_prices, 'price changed', 'prices changed')
  add(summary.changed_asps, 'ASP changed', 'ASPs changed')
  add(summary.added_levels, 'price level added', 'price levels added')
  add(summary.renamed_levels, 'price level renamed', 'price levels renamed')
  add(summary.removed_levels, 'price level removed', 'price levels removed')
  return parts.length > 0 ? parts.join(', ') : 'No changes against the published pricings'
}

// A published cell value differs from the draft's. A missing cell is null on
// either side.
export function cellChanged(now: number | null | undefined, was: number | null | undefined): boolean {
  return (now ?? null) !== (was ?? null)
}

// Whether a kept row has any change worth showing.
export function rowChanged(row: Row, diff: PricingDiff): boolean {
  const id = row.product_external_id
  if (diff.added_product_ids.includes(id) || id in diff.moved) return true
  if (cellChanged(row.asp?.price, diff.asps_was[id])) return true
  const was = diff.prices_was[id] ?? {}
  return Object.keys(was).some((levelId) => cellChanged(row.prices_by_level_id[levelId]?.price, was[levelId]))
}

// The published value under a changed cell, GitHub-style: red strikethrough
// "was …", or "new" in green when the cell was empty before.
export function WasLine({ was, currency }: { was: number | null | undefined; currency: string }) {
  if (was == null) return <div className="mt-0.5 text-[10px] font-medium" style={{ color: 'var(--success)' }}>new</div>
  return (
    <div className="mt-0.5 text-[10px] tabular-nums line-through" style={{ color: 'var(--destructive)' }}>
      was {formatMoneyFull(was, currency)}
    </div>
  )
}
